import { useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { FLAVORS } from "../data/products";
import { useStore } from "../context/StoreContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewForm = ({ onPosted }) => {
  const { t, lang, flavor: activeFlavor } = useStore();
  const [form, setForm] = useState({ name: "", sport: "", flavor: activeFlavor, rating: 0, text: "", website: "" });
  const [hover, setHover] = useState(0);
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim().length >= 2 && form.rating > 0 && form.text.trim().length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setStatus("error"); setMsg(t.reviews.validation); return; }
    setStatus("sending");
    try {
      const res = await axios.post(`${API}/reviews`, { ...form, lang });
      setStatus("done");
      setMsg(t.reviews.thanks);
      onPosted(res.data);
    } catch (err) {
      setStatus("error");
      setMsg(err?.response?.status === 429 ? t.reviews.tooMany : t.reviews.error);
    }
  };

  const inputCls = "field w-full px-3 py-2.5 text-sm";
  const labelCls = "mb-1 block text-xs font-semibold text-[var(--muted)]";

  return (
    <form onSubmit={submit} className="card mb-8 grid grid-cols-1 gap-5 p-5 sm:p-6 lg:grid-cols-[1fr_1.4fr]" data-testid="review-form" noValidate>
      <div className="space-y-4">
        <div>
          <label className={labelCls} htmlFor="rv-name">{t.reviews.name}</label>
          <input id="rv-name" className={inputCls} value={form.name} onChange={set("name")} maxLength={60} data-testid="review-name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="rv-sport">{t.reviews.sport}</label>
          <input id="rv-sport" className={inputCls} value={form.sport} onChange={set("sport")} maxLength={60} data-testid="review-sport" />
        </div>
        <div>
          <span className={labelCls}>{t.reviews.flavor}</span>
          <div className="flex gap-2">
            {Object.values(FLAVORS).map((f) => (
              <button
                type="button"
                key={f.id}
                onClick={() => setForm((x) => ({ ...x, flavor: f.id }))}
                className={`flex-1 border px-3 py-2 text-sm font-semibold transition-colors ${form.flavor === f.id ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-[var(--line)] bg-white text-[var(--muted)]"}`}
                data-testid={`review-flavor-${f.id}`}
              >
                {f.name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className={labelCls}>{t.reviews.rating}</span>
          <div className="flex gap-1" data-testid="review-rating" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => {
              const on = n <= (hover || form.rating);
              return (
                <button type="button" key={n} onMouseEnter={() => setHover(n)} onClick={() => setForm((x) => ({ ...x, rating: n }))} className="p-0.5" aria-label={`${n} / 5`} data-testid={`review-star-${n}`}>
                  <Star className={`h-7 w-7 ${on ? "accent-text" : "text-[var(--line)]"}`} fill={on ? "currentColor" : "none"} />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        <label className={labelCls} htmlFor="rv-text">{t.reviews.text}</label>
        <textarea id="rv-text" className={`${inputCls} min-h-[160px] flex-1 resize-none`} value={form.text} onChange={set("text")} maxLength={600} placeholder={t.reviews.textPlaceholder} data-testid="review-text" />
        <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-[11px] text-[var(--muted)]">{form.text.length}/600</span>
          <button type="submit" disabled={status === "sending"} className="btn-primary px-6 py-3 text-sm" data-testid="review-submit">
            {status === "sending" ? t.reviews.sending : t.reviews.submit}
          </button>
        </div>
        {msg && (
          <p className={`mt-3 text-sm ${status === "error" ? "text-red-600" : "text-green-700"}`} data-testid="review-message" role="status">{msg}</p>
        )}
      </div>
    </form>
  );
};

export default ReviewForm;
